export const FGTN_QR_FONT = 'FGTN Roboto Black';
const FONT = `'${FGTN_QR_FONT}', Arial, Helvetica, sans-serif`;
const BLUE = '#0F50F2';
export const FGTN_QR_PNG_SIZE = 1080;
const FONT_RATIO = 0.088;
const BAR_RATIO = 0.146;
const SUPPORT_RATIO = 0.028;
const LAUNCH_FONT_RATIO = 0.07;
const LAUNCH_BAR_RATIO = 0.2;
const LAUNCH_SUPPORT_RATIO = 0.048;
const LAUNCH_URL_RATIO = 0.072;

export function getShareQrMetrics(variant = 'default') {
  if (variant === 'launch') {
    return {
      fontRatio: LAUNCH_FONT_RATIO,
      barRatio: LAUNCH_BAR_RATIO,
      supportRatio: LAUNCH_SUPPORT_RATIO,
      urlRatio: LAUNCH_URL_RATIO,
      minHeading: 18,
      minSupport: 15,
      minUrl: 20,
      wrapHeadline: true,
    };
  }
  return {
    fontRatio: FONT_RATIO,
    barRatio: BAR_RATIO,
    supportRatio: SUPPORT_RATIO,
    urlRatio: 0.042,
    minHeading: 16,
    minSupport: 10,
    minUrl: 12,
    wrapHeadline: false,
  };
}

function measureLine(ctx, text, size, weight) {
  ctx.font = `${weight} ${size}px ${FONT}`;
  const metrics = ctx.measureText(text);
  const height = Math.ceil(
    (metrics.actualBoundingBoxAscent || size * 0.8) +
      (metrics.actualBoundingBoxDescent || size * 0.2)
  );
  return { width: metrics.width, height };
}

function fontSizeForBar(ctx, text, maxWidth, ratio = FONT_RATIO) {
  const designed = Math.round(maxWidth * ratio);
  let size = designed;
  while (size > 12 && measureLine(ctx, text, size, '900').width > maxWidth * 0.92) {
    size -= 1;
  }
  return size;
}

export function fgtnQrBarHeight(qrSize, extraLines = 0, variant = 'default') {
  const { barRatio } = getShareQrMetrics(variant);
  const base = Math.max(40, Math.round(qrSize * barRatio));
  if (extraLines > 1) {
    const grow = variant === 'launch' ? 0.86 + extraLines * 0.34 : 0.7 + extraLines * 0.38;
    return Math.max(base, Math.round(base * grow));
  }
  return base;
}

export function fgtnQrBarFontSize(qrSize, variant = 'default') {
  const { fontRatio, minHeading } = getShareQrMetrics(variant);
  return Math.max(minHeading, Math.round(qrSize * fontRatio));
}

export function fitQrBarFontSize(qrSize, text, variant = 'default') {
  const designed = fgtnQrBarFontSize(qrSize, variant);
  if (variant === 'launch') return designed;
  const maxWidth = qrSize * 0.9;
  let size = designed;
  const estimatedWidth = (value) => String(text || '').length * value * 0.62;
  while (size > 10 && estimatedWidth(size) > maxWidth) {
    size -= 1;
  }
  return size;
}

export function shareQrSupportFontSize(qrSize, variant = 'default') {
  const { supportRatio, minSupport } = getShareQrMetrics(variant);
  return Math.max(minSupport, Math.round(qrSize * supportRatio));
}

export function shareQrUrlFontSize(qrSize, variant = 'default') {
  const { urlRatio, minUrl } = getShareQrMetrics(variant);
  return Math.max(minUrl, Math.round(qrSize * urlRatio));
}

async function waitForRoboto() {
  if (typeof document === 'undefined' || !document.fonts) return;
  try {
    await document.fonts.load(`900 64px "${FGTN_QR_FONT}"`);
    await document.fonts.ready;
  } catch (err) {
    console.warn('Roboto Black did not load for QR export', err);
  }
}

function shareLinesFromOptions({ caption, supportingText, displayUrl, extraLines }) {
  if (Array.isArray(extraLines) && extraLines.length) {
    return extraLines.filter(Boolean);
  }
  return [supportingText, caption, displayUrl].filter(Boolean);
}

function splitHeadline(ctx, heading, maxWidth, size) {
  const words = String(heading || '').split(/\s+/).filter(Boolean);
  if (words.length < 2) return [heading];
  const fits = measureLine(ctx, heading, size, '900').width <= maxWidth * 0.9;
  if (fits && words.length < 4) return [heading];
  const mid = Math.ceil(words.length / 2);
  return [words.slice(0, mid).join(' '), words.slice(mid).join(' ')];
}

function drawLaunchShareQr({
  canvas,
  qrImage,
  headline,
  caption,
  supportingText,
  displayUrl,
  extraLines,
  barColor,
  bottomBarColor,
}) {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  const heading = headline || '';
  const bottomLines = shareLinesFromOptions({ caption, supportingText, displayUrl, extraLines });
  const headingSize = fontSizeForBar(ctx, heading, FGTN_QR_PNG_SIZE, LAUNCH_FONT_RATIO);
  const titleLines = splitHeadline(ctx, heading, FGTN_QR_PNG_SIZE, headingSize);
  const topBar = Math.max(
    Math.round(FGTN_QR_PNG_SIZE * LAUNCH_BAR_RATIO),
    titleLines.length > 1 ? Math.round(headingSize * 2.6) : Math.round(FGTN_QR_PNG_SIZE * LAUNCH_BAR_RATIO)
  );
  const bottomBar = fgtnQrBarHeight(FGTN_QR_PNG_SIZE, bottomLines.length, 'launch');

  canvas.width = FGTN_QR_PNG_SIZE;
  canvas.height = topBar + FGTN_QR_PNG_SIZE + bottomBar;

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  if (qrImage) {
    ctx.drawImage(qrImage, 0, topBar, FGTN_QR_PNG_SIZE, FGTN_QR_PNG_SIZE);
  }

  ctx.fillStyle = barColor || BLUE;
  ctx.fillRect(0, 0, FGTN_QR_PNG_SIZE, topBar);
  ctx.fillStyle = bottomBarColor || barColor || BLUE;
  ctx.fillRect(0, topBar + FGTN_QR_PNG_SIZE, FGTN_QR_PNG_SIZE, bottomBar);

  ctx.fillStyle = '#ffffff';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  if (ctx.letterSpacing !== undefined) ctx.letterSpacing = '0.03em';
  titleLines.forEach((line, index) => {
    ctx.font = `900 ${headingSize}px ${FONT}`;
    const y = (topBar * (index + 1)) / (titleLines.length + 1);
    ctx.fillText(line, FGTN_QR_PNG_SIZE / 2, y);
  });

  const lineCount = Math.max(bottomLines.length, 1);
  bottomLines.forEach((line, index) => {
    const isUrl = /at\.fish\//i.test(line);
    const size = isUrl
      ? fontSizeForBar(ctx, line, FGTN_QR_PNG_SIZE, LAUNCH_URL_RATIO)
      : fontSizeForBar(ctx, line, FGTN_QR_PNG_SIZE, LAUNCH_SUPPORT_RATIO);
    ctx.font = `900 ${size}px ${FONT}`;
    const y =
      topBar +
      FGTN_QR_PNG_SIZE +
      (bottomBar * (index + 1)) / (lineCount + 1);
    ctx.fillText(line, FGTN_QR_PNG_SIZE / 2, y);
  });
}

export function drawLabeledShareQr({
  canvas,
  qrImage,
  headline,
  caption,
  supportingText,
  displayUrl,
  extraLines,
  barColor = BLUE,
  bottomBarColor,
  variant = 'default',
}) {
  if (variant === 'launch') {
    drawLaunchShareQr({
      canvas,
      qrImage,
      headline,
      caption,
      supportingText,
      displayUrl,
      extraLines,
      barColor,
      bottomBarColor,
    });
    return;
  }

  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  const heading = headline || '';
  const bottomLines = shareLinesFromOptions({ caption, supportingText, displayUrl, extraLines });
  const topBar = Math.round(FGTN_QR_PNG_SIZE * BAR_RATIO);
  const bottomBar = fgtnQrBarHeight(FGTN_QR_PNG_SIZE, bottomLines.length);
  const headingSize = fontSizeForBar(ctx, heading, FGTN_QR_PNG_SIZE);

  canvas.width = FGTN_QR_PNG_SIZE;
  canvas.height = topBar + FGTN_QR_PNG_SIZE + bottomBar;

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  if (qrImage) {
    ctx.drawImage(qrImage, 0, topBar, FGTN_QR_PNG_SIZE, FGTN_QR_PNG_SIZE);
  }

  ctx.fillStyle = barColor || BLUE;
  ctx.fillRect(0, 0, FGTN_QR_PNG_SIZE, topBar);
  ctx.fillStyle = bottomBarColor || barColor || BLUE;
  ctx.fillRect(0, topBar + FGTN_QR_PNG_SIZE, FGTN_QR_PNG_SIZE, bottomBar);

  ctx.fillStyle = '#ffffff';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.font = `900 ${headingSize}px ${FONT}`;
  if (ctx.letterSpacing !== undefined) ctx.letterSpacing = '0.03em';
  ctx.fillText(heading, FGTN_QR_PNG_SIZE / 2, topBar / 2);

  const lineCount = Math.max(bottomLines.length, 1);
  bottomLines.forEach((line, index) => {
    const isUrl = /at\.fish\//i.test(line);
    const size = isUrl
      ? fontSizeForBar(ctx, line, FGTN_QR_PNG_SIZE, 0.042)
      : fontSizeForBar(ctx, line, FGTN_QR_PNG_SIZE, lineCount > 1 ? 0.034 : FONT_RATIO);
    ctx.font = `900 ${size}px ${FONT}`;
    const y =
      topBar +
      FGTN_QR_PNG_SIZE +
      (bottomBar * (index + 1)) / (lineCount + 1);
    ctx.fillText(line, FGTN_QR_PNG_SIZE / 2, y);
  });
}

export function drawLabeledFgtnQr({ canvas, qrImage, episodeNumber }) {
  drawLabeledShareQr({
    canvas,
    qrImage,
    headline: `FGTN Episode ${episodeNumber}`,
    caption: 'Scan for links',
  });
}

export async function downloadLabeledShareQrPng({
  qrCanvas,
  headline,
  caption,
  supportingText,
  displayUrl,
  extraLines,
  filename,
  episodeNumber,
  barColor,
  bottomBarColor,
  variant,
}) {
  if (!qrCanvas) return;

  await waitForRoboto();

  const canvas = document.createElement('canvas');
  drawLabeledShareQr({
    canvas,
    qrImage: qrCanvas,
    headline: headline || (episodeNumber ? `FGTN Episode ${episodeNumber}` : ''),
    caption,
    supportingText,
    displayUrl,
    extraLines,
    barColor,
    bottomBarColor,
    variant,
  });

  const link = document.createElement('a');
  link.href = canvas.toDataURL('image/png');
  link.download =
    filename ||
    (episodeNumber ? `FGTN-Episode-${episodeNumber}-QR.png` : 'angler-built-qr.png');
  link.click();
}

export async function downloadLabeledFgtnQrPng({ qrCanvas, episodeNumber }) {
  return downloadLabeledShareQrPng({
    qrCanvas,
    episodeNumber,
    headline: `FGTN Episode ${episodeNumber}`,
    caption: 'Scan for links',
  });
}
